// =============================================================================
// HistoricoCliente — o que este cliente já comprou.
//
// Pedido da equipe comercial (reunião de 24/08/2026). No balcão, saber o que o
// cliente já levou muda a conversa: "você levou 30 sacos por R$ 30,90 no mês
// passado" é a diferença entre negociar no escuro e negociar sabendo.
//
// Abre a partir do bloco CLIENTE do documento, sem sair do orçamento — pela
// mesma razão de tudo o mais aqui: a decisão acontece no papel, e mandar o
// vendedor para outra tela no meio da venda é perder o fio.
//
// ⚠️ São 12 meses e SÓ VENDA — orçamento não conta como compra (ver migration
//    014). Pedido cancelado aparece marcado, em vez de sumir: pedido que some
//    sem explicação vira telefonema.
//
// ⚠️ A tela DIZ DE QUANDO É O DADO. Isto é espelho, tão fresco quanto o último
//    sync; sem essa frase alguém jura que o cliente não comprou ontem.
// =============================================================================

import { useEffect, useState } from 'react';
import api, { mensagemDeErro } from '../services/api';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const qtd = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const dia = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');
const diaHora = (v) => (v ? `${dia(v)} ${String(v).slice(11, 16)}` : '—');

export default function HistoricoCliente({ cliente, aoFechar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') aoFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aoFechar]);

  useEffect(() => {
    let cancelado = false;
    api.get(`/api/clientes/${cliente.codclie}/compras`, { params: { limite: 20 } })
      .then((r) => { if (!cancelado) setDados(r.data); })
      .catch((e) => { if (!cancelado) setErro(mensagemDeErro(e, 'Não deu para buscar o histórico.')); });
    return () => { cancelado = true; };
  }, [cliente.codclie]);

  const compras = dados?.compras || [];

  return (
    <div className="historico nao-imprimir" onClick={aoFechar} role="dialog" aria-label="Histórico de compras">
      <div className="historico__painel" onClick={(e) => e.stopPropagation()}>
        <div className="historico__topo">
          <div>
            <h2 className="historico__titulo">Histórico de compra</h2>
            <div className="historico__sub">{cliente.razao} · cód. {cliente.codclie}</div>
          </div>
          <button type="button" className="historico__fechar" onClick={aoFechar} aria-label="Fechar">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {dados?.resumo?.compras > 0 && (
          <div className="historico__resumo">
            <span><strong>{dados.resumo.compras}</strong> compra(s) em 12 meses</span>
            <span><strong>{dinheiro(dados.resumo.total)}</strong> no período</span>
            <span>última em <strong>{dia(dados.resumo.ultima)}</strong></span>
          </div>
        )}

        <div className="historico__corpo">
          {!dados && !erro && (
            <div className="text-center py-5 text-muted">
              <span className="spinner-border spinner-border-sm me-2" />Buscando…
            </div>
          )}
          {erro && (
            <div className="erp-doc__msg erp-doc__msg--erro">
              <i className="bi bi-x-circle-fill" /><span>{erro}</span>
            </div>
          )}

          {dados && compras.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-bag display-6 d-block mb-2 opacity-50" />
              Sem compra nos últimos 12 meses.
              <div className="small mt-1">O histórico cobre um ano e só conta venda — orçamento não entra.</div>
            </div>
          )}

          {compras.map((c) => (
            <div key={c.numped} className={`historico__compra${c.cancelado ? ' e-cancelada' : ''}`}>
              <div className="historico__compra-bar">
                <span className="historico__compra-data">{diaHora(c.dtpedido)}</span>
                <span className="badge badge-filial">{c.filial}</span>
                {c.vendedor_nome && <span className="historico__compra-vend">{c.vendedor_nome}</span>}
                {c.cancelado ? <span className="historico__cancelada">cancelado</span> : null}
                <span className="historico__compra-valor">{dinheiro(c.valor)}</span>
              </div>
              <table className="historico__itens">
                <tbody>
                  {c.itens.map((i, n) => (
                    <tr key={`${c.numped}-${i.codpro}-${n}`}>
                      <td className="historico__it-desc">
                        {i.descricao || <span className="text-muted">produto {i.codpro}</span>}
                      </td>
                      <td className="historico__it-qtd">{qtd(i.quantidade)} {i.unidade}</td>
                      <td className="historico__it-preco">{dinheiro(i.preco_unitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* Dizer de onde e de quando é o dado. Desde 29/10/2026 é lido do
            Microuniverso na hora; antes era espelho, e a frase dizia que compra
            recente podia ainda não estar aqui — o que deixou de ser verdade. */}
        {dados?.espelho?.em && (
          <div className="historico__rodape">
            <i className="bi bi-info-circle me-1" />
            Lido agora do Microuniverso ({diaHora(dados.espelho.em)}). Só conta venda — orçamento não entra.
          </div>
        )}
      </div>
    </div>
  );
}
